import type { ReactNode } from "react";
import { BrandMark } from "@/components/brand/BrandMark";
import { useAuth } from "@/lib/auth";
import { SignInScreen } from "@/screens/SignInScreen";

/**
 * The gate. Renders nothing but a holding screen while Amplify decides
 * whether there is a session, then either the console or the sign-in form.
 *
 * There is no redirect and no `/login` route: the console is one origin with
 * one entry point, and a URL that says "signed out" is a URL somebody can
 * bookmark and be confused by.
 */
export function ProtectedRoute({ children }: { children: ReactNode }) {
  const { status } = useAuth();

  if (status === "loading") {
    return (
      <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-brand-pink">
        <div
          aria-hidden="true"
          className="absolute -left-24 top-[-10%] h-[56vh] w-[56vh] rounded-full bg-brand-green"
        />
        <div
          aria-hidden="true"
          className="absolute -bottom-32 -right-16 h-[45vh] w-[45vh] rounded-full border-[70px] border-white/75"
        />
        <div className="relative flex flex-col items-center gap-3">
          <BrandMark className="h-24 w-24 animate-pulse" />
          <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-on-brand/65">
            Checking session
          </p>
        </div>
      </div>
    );
  }

  if (status === "signed_out") return <SignInScreen />;
  return <>{children}</>;
}
